<script>
/*  */
import SvgIcon from '@/components/SvgIcon';
/*  */
import { mapState } from 'vuex';
import Simplebar from 'simplebar-vue';
import { flagNameFromCountry } from '@/scripts/utils';

export default {
  name: 'Dropdown',
  components: {
    Simplebar,
    SvgIcon,
  },
  props: {
    id: {
      type: String,
    },
    items: {
      type: Array,
    },
    forceValue: {
      type: Object,
    },
    titleField: {
      type: String,
      default: 'title',
    },
    valueField: {
      type: String,
      default: 'value',
    },
    placeholder: {
      type: String,
      default: '',
    },
    styleType: {
      type: String,
      default: 'basic',
    },
    type: {
      type: String,
      default: '',
    },
    setFirst: {
      type: Boolean,
      default: true,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    calendarIcon: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    const { items, placeholder, forceValue } = this;

    return {
      active: false,
      value: forceValue
        ? forceValue
        : placeholder
        ? null
        : items
        ? items[0]
        : null,
    };
  },
  watch: {
    items(items) {
      this.active = false;

      if (!items) return;

      const [first] = items;

      this.value = first;
    },
    value(val) {
      this.$emit('change', val);
    },
    forceValue(forceValue) {
      this.value = forceValue;
    },
  },
  computed: {
    ...mapState([]),
    title() {
      const { value, titleField, placeholder } = this;
      const result = value ? value[titleField] : placeholder;

      return result;
    },
    isCalendar() {
      const { items, calendarIcon } = this;
      const result =
        calendarIcon || (items && items[0] && items[0].title === 'All time');

      return result;
    },
  },
  created() {
    const { setFirst, value } = this;

    if (setFirst && value) this.$emit('change', value);
  },
  mounted() {},
  methods: {
    toggleSelect() {
      const { disabled } = this;

      if (disabled) return;

      this.active = !this.active;
    },
    changeSelect(item) {
      /*  */
      if (!item) return;

      this.active = false;
      this.value = item;
    },
    flagNameFromCountry,
  },
};
</script>

<template>
  <div
    class="dropdown"
    :class="[
      { 'is-open': active, disabled: disabled },
      'dropdown--' + styleType,
    ]"
    @click="toggleSelect"
  >
    <div class="dropdown__toggle">
      <img
        v-if="isCalendar"
        :src="require('@/assets/svg/calendar.svg')"
        class="dropdown__icon"
      />
      <div
        :class="{
          dropdown__selected: true,
          placeholder: placeholder && !value,
        }"
      >
        <div class="dropdown__flag" v-if="value && type === 'country'">
          <SvgIcon :name="flagNameFromCountry(value.title)"></SvgIcon>
        </div>
        {{ title }}
      </div>
      <img
        :src="require('@/assets/svg/triangle-yellow.svg')"
        class="dropdown__arrow"
      />
    </div>
    <div class="dropdown__menu">
      <div class="dropdown__content">
        <Simplebar data-simplebar-auto-hide="false" class="scroll">
          <div
            class="dropdown__item"
            v-for="item in items"
            :class="{ 'is-active': value && item.title === value.title }"
            :key="item[valueField]"
            @click.stop="changeSelect(item)"
          >
            <div class="dropdown__flag" v-if="type === 'country'">
              <SvgIcon :name="flagNameFromCountry(item.title)"></SvgIcon>
            </div>
            {{ item[titleField] }}
          </div>
        </Simplebar>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
@import '@/styles/variables.scss';
.dropdown {
  position: relative;
  z-index: 2;

  &.is-open {
    .dropdown__menu {
      display: block;
    }

    .dropdown__toggle {
      border-bottom-left-radius: 0;
      border-bottom-right-radius: 0;
    }

    .dropdown__arrow {
      transform: translateY(-50%) rotate(0);
    }
  }

  &__toggle {
    width: 100%;
    height: 44px;
    border-radius: 12px;
    background: #1d2429;
    display: flex;
    align-items: center;
    padding: 10px 35px 10px 15px;
    color: #fff;
    overflow: hidden;
    cursor: pointer;
    position: relative;
  }

  &__icon {
    display: flex;
    width: 20px;
    height: 20px;
    flex-shrink: 0;
    margin-right: 10px;
  }

  &__arrow {
    width: 10px;
    height: 7px;
    display: flex;
    position: absolute;
    top: 50%;
    right: 15px;
    transform: translateY(-50%) rotate(180deg);
    user-select: none;
  }

  &__selected {
    font-family: Poppins-Regular;
    font-size: 14px;
    line-height: 20px;
    overflow: hidden;
    text-transform: capitalize;
    display: flex;
    align-items: center;
    white-space: nowrap;

    &.placeholder {
      color: rgba(255, 255, 255, 0.5);
    }
  }

  &__menu {
    background: #1d2429;
    border-bottom-left-radius: 12px;
    border-bottom-right-radius: 12px;
    display: none;
    position: absolute;
    top: 100%;
    left: 0;
    width: 100%;
    margin-bottom: 40px;
  }

  &__content {
    height: auto;
    padding: 18px 0 15px;
    position: relative;

    &:before {
      content: '';
      position: absolute;
      top: 0;
      left: 50%;
      transform: translateX(-50%);
      width: calc(100% - 20px);
      height: 1px;
      background: rgba(255, 255, 255, 0.2);
    }

    .scroll {
      max-height: 108px;
    }
  }

  &__item {
    min-height: 27px;
    display: flex;
    align-items: center;
    padding: 3px 15px;
    color: rgba(255, 255, 255, 0.7);
    font-family: Poppins-Regular;
    font-size: 14px;
    line-height: 20px;
    cursor: pointer;
    transition: all 0.2s;

    &.is-active,
    &:hover {
      color: #ffffff;
    }
  }

  &__flag {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 16px;
    height: 14px;
    margin-right: 10px;

    .svg-icon {
      width: 16px;
      height: 14px;
    }
  }

  &--form {
    .dropdown__toggle {
      background-color: #272727;
      border: solid 1px rgba(255, 255, 255, 0.2);
      border-radius: 8px;
    }

    .dropdown__content {
      padding: 15px 0 10px;
    }

    &.is-open {
      .dropdown__toggle {
        border-bottom-left-radius: 0;
        border-bottom-right-radius: 0;
        border-bottom: 1px solid transparent;
      }
    }
    .dropdown__menu {
      border-bottom-left-radius: 8px;
      border-bottom-right-radius: 8px;
      background-color: #272727;
      border: solid 1px rgba(255, 255, 255, 0.2);
      border-top: 0;

      &:before {
        content: '';
        border-bottom-left-radius: 8px;
        border-bottom-right-radius: 8px;
        position: absolute;
        width: calc(100% + 2px);
        height: 100%;
        background-color: #121516;
        z-index: -1;
        top: 0;
        left: -1px;
      }
    }
  }

  &.disabled {
    .dropdown__toggle {
      pointer-events: none;
      user-select: none;
    }
    .dropdown__arrow {
      opacity: 0.5;
    }
  }
}
</style>
